/**
 * Modele de description des ressources.
 *
 * Le backend de ce projet est ecrit en controleurs `#[Route]` minces sur Doctrine
 * DBAL, **sans aucune ressource `#[ApiResource]`** : la documentation Hydra
 * d'API Platform ne decrit donc rien, et l'introspection automatique n'a rien a
 * trouver (voir `src/discovery/hydraDiscovery.ts`).
 *
 * Ce fichier definit le vocabulaire qui comble l'ecart : chaque ressource reelle
 * est decrite une fois — champs, types, filtres, operations reellement servies —
 * et les ecrans se generent depuis cette description. Ajouter une ressource ne
 * demande donc pas d'ecrire un ecran.
 *
 * Regle stricte : **une description ne declare que ce que le serveur sert
 * vraiment.** Annoncer une edition qu'aucune route `PATCH` ne porte fabriquerait
 * un ecran qui echoue a l'enregistrement — exactement l'« ecran fictif presente
 * comme termine » que CLAUDE.md §7 interdit.
 */

/** Nature d'un champ, qui determine le rendu en lecture et en saisie. */
export type FieldKind =
  | 'identifier'
  | 'text'
  | 'longText'
  | 'email'
  | 'integer'
  | 'milliliters'
  | 'grams'
  | 'millimeters'
  /**
   * Champ stocké et transmis en millimètres, mais saisi et affiché en
   * centimètres (#373) — la conversion vit dans `fields.tsx`/`inputs.tsx`,
   * jamais dans le descripteur, qui ne fait qu'annoncer la nature du champ.
   */
  | 'centimeters'
  | 'celsius'
  | 'months'
  | 'seconds'
  | 'boolean'
  | 'date'
  | 'dateTime'
  | 'choice'
  | 'reference'
  | 'stringList'
  | 'json';

/** Quand un champ peut-il etre ecrit ? */
export type Writability =
  /** Calcule par le serveur : jamais dans un formulaire. */
  | 'readOnly'
  /** Accepte a la creation seulement — aucune route ne le remet a jour. */
  | 'createOnly'
  /** Accepte a la creation et a la mise a jour. */
  | 'always';

export interface Choice {
  readonly id: string;
  readonly name: string;
}

export interface FieldDescriptor {
  readonly name: string;
  readonly label: string;
  readonly kind: FieldKind;
  readonly writability: Writability;
  /** Presence dans le tableau. Un tableau de trente colonnes n'est pas lisible. */
  readonly listed?: boolean;
  /** Obligatoire cote serveur en toutes circonstances. */
  readonly required?: boolean;
  /**
   * Obligatoire seulement dans certains cas, evalue sur les valeurs du
   * formulaire.
   *
   * Necessaire parce qu'une meme collection peut avoir deux routes de creation
   * aux contraintes differentes : `POST .../feeding-sessions/bottle` exige la
   * quantite proposee et le type de lait, `POST .../feeding-sessions/breastfeeding`
   * ne les accepte meme pas. Sans cette notion, l'un des deux formulaires
   * mentirait — soit en exigeant un champ inutile, soit en laissant partir une
   * requete que le serveur refuse en `validation_failed`.
   */
  readonly requiredWhen?: (values: Readonly<Record<string, unknown>>) => boolean;
  /**
   * Champ masque tant que ce predicat refuse les valeurs du formulaire. Les
   * durees par sein n'ont aucun sens sur un biberon.
   */
  readonly visibleWhen?: (values: Readonly<Record<string, unknown>>) => boolean;
  /** Valeurs admises, pour `kind: 'choice'`. Reprises des enums PHP du domaine. */
  readonly choices?: readonly Choice[];
  /** Ressource visee, pour `kind: 'reference'`. */
  readonly reference?: string;
  /** Aide affichee sous le champ : unite, contrainte, origine de la donnee. */
  readonly helperText?: string;
}

/** Regroupement dans le menu lateral. */
export type ResourceGroup = 'identite' | 'stock' | 'suivi' | 'audit' | 'legal';

/** Comment `getOne` obtient un enregistrement. */
export type SingleRecordStrategy =
  /** Le serveur expose `GET <chemin>/{id}`. */
  | 'endpoint'
  /**
   * Aucune route unitaire : l'enregistrement est extrait de la collection.
   * Acceptable parce que ces collections sont bornees a un profil bebe, jamais
   * a l'echelle de la base.
   */
  | 'fromCollection';

/** Pagination reellement offerte par la route de liste. */
export type PaginationStrategy =
  /** La route accepte `limit`/`offset` et renvoie `hasMore`. */
  | 'server'
  /**
   * La route renvoie `{items: [...]}` en entier. Le tableau pagine, filtre et
   * trie donc **cote client**. Assume et documente : inventer un `total` ou une
   * page serveur inexistante donnerait des resultats faux.
   */
  | 'client';

/** Action non-CRUD servie par une route dediee. */
export interface ResourceAction {
  readonly name: string;
  readonly label: string;
  /** Suffixe ajoute a `<chemin>/{id}`. Exemple : `archive`. */
  readonly suffix: string;
  readonly method: 'POST' | 'DELETE';
  /** Message de confirmation. Absent = action appliquee sans confirmation. */
  readonly confirmation?: string;
  /**
   * L'action n'est proposee que si ce predicat accepte l'enregistrement : une
   * restauration sur un enregistrement actif n'a pas de sens.
   */
  readonly appliesTo?: (record: Readonly<Record<string, unknown>>) => boolean;
}

export interface ResourceDescriptor {
  /** Nom de la ressource React Admin, aussi segment d'URL du backoffice. */
  readonly name: string;
  readonly label: string;
  readonly group: ResourceGroup;
  /**
   * Chemin API, relatif a la base. `{babyId}` est substitue par le profil bebe
   * selectionne.
   */
  readonly path: string;
  readonly capabilities: {
    readonly list: boolean;
    readonly show: boolean;
    readonly create: boolean;
    readonly edit: boolean;
  };
  readonly singleRecord: SingleRecordStrategy;
  readonly pagination: PaginationStrategy;
  /**
   * Parametres de requete reellement interpretes par la route de liste. Les
   * autres filtres, s'il y en a, sont appliques cote client.
   */
  readonly serverFilters: readonly string[];
  /**
   * La creation exige une cle d'idempotence dans le corps JSON. Le projet la
   * transporte dans le corps, jamais dans un en-tete (CLAUDE.md §4).
   */
  readonly requiresIdempotencyKey?: boolean;
  /**
   * Suffixe de la route de creation, calcule depuis le formulaire. Les seances
   * d'alimentation ont deux routes de creation (`/bottle`, `/breastfeeding`)
   * pour une seule collection en lecture.
   */
  readonly createSuffix?: (values: Readonly<Record<string, unknown>>) => string;
  /**
   * Champs a retirer du corps avant l'envoi : ils pilotent le formulaire sans
   * exister cote serveur.
   */
  readonly omitOnCreate?: readonly string[];
  readonly actions?: readonly ResourceAction[];
  readonly fields: readonly FieldDescriptor[];
  /** Explication affichee en tete de liste : limites, contenu, mise en garde. */
  readonly notice?: string;
}

/** Une ressource dont le chemin porte `{babyId}` depend du profil selectionne. */
export const isBabyScoped = (descriptor: ResourceDescriptor): boolean =>
  descriptor.path.includes('{babyId}');

/**
 * Substitue le profil bebe dans le chemin.
 *
 * @throws Error si la ressource est portee par un bebe et qu'aucun n'est
 *         selectionne. Lever ici vaut mieux qu'appeler `/babies//milk-batches`,
 *         qui rendrait un 404 incomprehensible. Le message renvoie a l'ecran de
 *         la ressource, qui porte le selecteur depuis qu'il a quitte la barre
 *         superieure : une consigne qui designe un endroit disparu est pire
 *         qu'une absence de consigne.
 */
export const resolvePath = (descriptor: ResourceDescriptor, babyId: string | null): string => {
  if (!isBabyScoped(descriptor)) {
    return descriptor.path;
  }

  if (babyId === null || babyId === '') {
    throw new Error(
      `La ressource « ${descriptor.label} » dépend d'un profil bébé. Sélectionnez-en un sur l'écran de la ressource.`,
    );
  }

  return descriptor.path.replace('{babyId}', encodeURIComponent(babyId));
};

/** Champs proposes a la saisie pour une creation. */
export const creatableFields = (descriptor: ResourceDescriptor): readonly FieldDescriptor[] =>
  descriptor.fields.filter((field) => field.writability !== 'readOnly');

/** Champs proposes a la saisie pour une mise a jour. */
export const editableFields = (descriptor: ResourceDescriptor): readonly FieldDescriptor[] =>
  descriptor.fields.filter((field) => field.writability === 'always');

/**
 * Colonnes du tableau.
 *
 * `listed: true` est explicite plutot que deduit : un tableau lisible se decide,
 * il ne se subit pas. Sans aucun champ marque, on retombe sur les six premiers
 * pour ne jamais rendre un tableau vide.
 */
export const listedFields = (descriptor: ResourceDescriptor): readonly FieldDescriptor[] => {
  const marked = descriptor.fields.filter((field) => field.listed === true);

  return marked.length > 0 ? marked : descriptor.fields.slice(0, 6);
};
